<script setup lang="ts">
const pageLocale = useState<string>('page-locale', () => 'fa')
const colorMode = useColorMode()
const isDark = computed(() => colorMode.value === 'dark')
const isAnimating = ref(false)

interface ViewTransitionDocument extends Document {
  startViewTransition?(callback: () => void | Promise<void>): { ready: Promise<void>; finished: Promise<void> }
}

function toggleWithAnimation(event: MouseEvent) {
  if (isAnimating.value) return

  const doc = document as ViewTransitionDocument
  if (!doc.startViewTransition || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    colorMode.preference = isDark.value ? 'light' : 'dark'
    return
  }

  isAnimating.value = true
  const x = event.clientX
  const y = event.clientY
  const endRadius = Math.hypot(
    Math.max(x, innerWidth - x),
    Math.max(y, innerHeight - y),
  )

  const transition = doc.startViewTransition(() => {
    colorMode.preference = isDark.value ? 'light' : 'dark'
  })

  transition.ready.then(() => {
    document.documentElement.animate(
      {
        clipPath: [
          `circle(0px at ${x}px ${y}px)`,
          `circle(${endRadius}px at ${x}px ${y}px)`,
        ],
      },
      {
        duration: 1000,
        easing: 'cubic-bezier(.76,.32,.29,.99)',
        pseudoElement: '::view-transition-new(root)',
      },
    )
  })

  transition.finished.finally(() => {
    isAnimating.value = false
  })
}
</script>

<template>
  <ClientOnly>
    <UButton
      :aria-label="isDark ? (pageLocale === 'fa' ? 'تغییر به حالت روز' : 'Switch to light') : (pageLocale === 'fa' ? 'تغییر به حالت شب' : 'Switch to dark')"
      color="neutral"
      variant="ghost"
      size="sm"
      square
      :icon="isDark ? 'i-lucide-sun' : 'i-lucide-moon'"
      class="rounded-full"
      :disabled="isAnimating"
      @click="toggleWithAnimation"
    />
    <template #placeholder>
      <div class="inline-flex size-8 items-center justify-center" />
    </template>
  </ClientOnly>
</template>

<style scoped>
::view-transition-new(root) {
  z-index: 100;
  animation: none;
  mix-blend-mode: normal;
}

::view-transition-old(root) {
  z-index: 1;
  animation: none;
  mix-blend-mode: normal;
}

@media (prefers-reduced-motion: reduce) {
  ::view-transition-new(root),
  ::view-transition-old(root) {
    animation: none !important;
  }
}
</style>
